import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { LoadingState } from './components/ui/LoadingState'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AuthGuard } from './components/AuthGuard'
import { useAuthStore } from './stores/auth'

// Eager: always needed
import { Dashboard } from './pages/Dashboard'

// Lazy: route-based code splitting
const Jobs = lazy(() => import('./pages/Jobs').then((m) => ({ default: m.Jobs })))
const JobDetail = lazy(() => import('./pages/JobDetail').then((m) => ({ default: m.JobDetail })))
const Quotes = lazy(() => import('./pages/Quotes').then((m) => ({ default: m.Quotes })))
const QuoteDetail = lazy(() => import('./pages/QuoteDetail').then((m) => ({ default: m.QuoteDetail })))
const Invoices = lazy(() => import('./pages/Invoices').then((m) => ({ default: m.Invoices })))
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail').then((m) => ({ default: m.InvoiceDetail })))
const Customers = lazy(() => import('./pages/Customers').then((m) => ({ default: m.Customers })))
const CustomerDetail = lazy(() => import('./pages/CustomerDetail').then((m) => ({ default: m.CustomerDetail })))
const Vehicles = lazy(() => import('./pages/Vehicles').then((m) => ({ default: m.Vehicles })))
const VehicleDetail = lazy(() => import('./pages/VehicleDetail').then((m) => ({ default: m.VehicleDetail })))
const Templates = lazy(() => import('./pages/Templates').then((m) => ({ default: m.Templates })))
const Catalog = lazy(() => import('./pages/Catalog').then((m) => ({ default: m.Catalog })))
const ServiceGroupsPage = lazy(() => import('./pages/ServiceGroups').then((m) => ({ default: m.ServiceGroups })))
const QuoteBuilder = lazy(() => import('./pages/QuoteBuilder').then((m) => ({ default: m.QuoteBuilder })))
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })))
const Profile = lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })))
const ShopMembers = lazy(() => import('./pages/ShopMembers').then((m) => ({ default: m.ShopMembers })))
const AdminConsole = lazy(() => import('./pages/AdminConsole').then((m) => ({ default: m.AdminConsole })))
const AdminSupport = lazy(() => import('./pages/AdminSupport').then((m) => ({ default: m.AdminSupport })))
const AdminCatalog = lazy(() => import('./pages/AdminCatalog').then((m) => ({ default: m.AdminCatalog })))
const Landing = lazy(() => import('./pages/Landing').then((m) => ({ default: m.Landing })))
const LandingA = lazy(() => import('./pages/LandingA').then((m) => ({ default: m.LandingA })))
const LandingB = lazy(() => import('./pages/LandingB').then((m) => ({ default: m.LandingB })))
const PublicQuote = lazy(() => import('./pages/PublicQuote').then((m) => ({ default: m.PublicQuote })))
const PublicJobApproval = lazy(() => import('./pages/PublicJobApproval').then((m) => ({ default: m.PublicJobApproval })))
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })))
const DemoLogin = lazy(() => import('./pages/DemoLogin').then((m) => ({ default: m.DemoLogin })))
const Onboarding = lazy(() => import('./pages/Onboarding').then((m) => ({ default: m.Onboarding })))
const Help = lazy(() => import('./pages/Help').then((m) => ({ default: m.Help })))
const Terms = lazy(() => import('./pages/Terms').then((m) => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/Privacy').then((m) => ({ default: m.Privacy })))
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })))

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingState />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

// Landing page for visitors; logged-in users go straight to the dashboard.
function LandingRoute() {
  const { isAuthenticated, isLoading, fetchUser } = useAuthStore()
  useEffect(() => {
    // Same session bootstrap as AuthGuard — this route mounts outside it,
    // so a fresh page load at / must initialize auth itself.
    if (!isAuthenticated && isLoading) {
      fetchUser()
    }
  }, [isAuthenticated, isLoading, fetchUser])
  if (isLoading) return <LoadingState fullPage />
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return (
    <SuspenseWrapper>
      <Landing />
    </SuspenseWrapper>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    children: [
      // Landing for visitors; logged-in users go straight to the dashboard.
      // Index route (not a duplicate '/' path) so matching doesn't depend on
      // declaration order.
      { index: true, element: <LandingRoute /> },
      {
        element: (
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        ),
        children: [
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'jobs', element: <SuspenseWrapper><Jobs /></SuspenseWrapper> },
      { path: 'jobs/:id', element: <SuspenseWrapper><JobDetail /></SuspenseWrapper> },
      { path: 'quotes', element: <SuspenseWrapper><Quotes /></SuspenseWrapper> },
      { path: 'quotes/:id', element: <SuspenseWrapper><QuoteDetail /></SuspenseWrapper> },
      { path: 'invoices', element: <SuspenseWrapper><Invoices /></SuspenseWrapper> },
      { path: 'invoices/:id', element: <SuspenseWrapper><InvoiceDetail /></SuspenseWrapper> },
      { path: 'customers', element: <SuspenseWrapper><Customers /></SuspenseWrapper> },
      { path: 'customers/:id', element: <SuspenseWrapper><CustomerDetail /></SuspenseWrapper> },
      { path: 'vehicles', element: <SuspenseWrapper><Vehicles /></SuspenseWrapper> },
      { path: 'vehicles/:id', element: <SuspenseWrapper><VehicleDetail /></SuspenseWrapper> },
      { path: 'templates', element: <SuspenseWrapper><Templates /></SuspenseWrapper> },
      { path: 'catalog', element: <SuspenseWrapper><Catalog /></SuspenseWrapper> },
      { path: 'service-groups', element: <SuspenseWrapper><ServiceGroupsPage /></SuspenseWrapper> },
      { path: 'settings', element: <SuspenseWrapper><Settings /></SuspenseWrapper> },
      { path: 'profile', element: <SuspenseWrapper><Profile /></SuspenseWrapper> },
      { path: 'shop-members', element: <SuspenseWrapper><ShopMembers /></SuspenseWrapper> },
      { path: 'admin', element: <SuspenseWrapper><AdminConsole /></SuspenseWrapper> },
      { path: 'admin/support', element: <SuspenseWrapper><AdminSupport /></SuspenseWrapper> },
      { path: 'admin/catalog', element: <SuspenseWrapper><AdminCatalog /></SuspenseWrapper> },
      { path: 'quotes/new', element: <SuspenseWrapper><QuoteBuilder /></SuspenseWrapper> },
      { path: 'quotes/:id/edit', element: <SuspenseWrapper><QuoteBuilder /></SuspenseWrapper> },
      { path: '*', element: <SuspenseWrapper><NotFound /></SuspenseWrapper> },
        ],
      },
    ],
  },
  {
    path: '/landing',
    element: <SuspenseWrapper><Landing /></SuspenseWrapper>,
  },
  {
    path: '/landing-a',
    element: <SuspenseWrapper><LandingA /></SuspenseWrapper>,
  },
  {
    path: '/landing-b',
    element: <SuspenseWrapper><LandingB /></SuspenseWrapper>,
  },
  {
    path: '/login',
    element: <SuspenseWrapper><Login /></SuspenseWrapper>,
  },
  {
    path: '/demo',
    element: <SuspenseWrapper><DemoLogin /></SuspenseWrapper>,
  },
  {
    path: '/onboarding',
    element: <SuspenseWrapper><Onboarding /></SuspenseWrapper>,
  },
  {
    path: '/help',
    element: <SuspenseWrapper><Help /></SuspenseWrapper>,
  },
  {
    path: '/terms',
    element: <SuspenseWrapper><Terms /></SuspenseWrapper>,
  },
  {
    path: '/terms-of-service',
    element: <SuspenseWrapper><Terms /></SuspenseWrapper>,
  },
  {
    path: '/privacy',
    element: <SuspenseWrapper><Privacy /></SuspenseWrapper>,
  },
  {
    path: '/privacy-policy',
    element: <SuspenseWrapper><Privacy /></SuspenseWrapper>,
  },
  {
    path: '/q/:token',
    element: <SuspenseWrapper><PublicQuote /></SuspenseWrapper>,
  },
  {
    path: '/approve/:token',
    element: <SuspenseWrapper><PublicJobApproval /></SuspenseWrapper>,
  },
])
